/**
 * SeriesThumbnail - Draggable series thumbnail
 * Shows 60x60 preview of middle slice with full series name, draggable to viewport slots
 */

import React, { useEffect, useMemo, useRef } from "react";
import { useAtomValue } from "jotai";
import { FaSpinner } from "../../utils/icons";
import { SeriesThumbnail as ThumbnailData } from "../../hooks/useSeriesThumbnails";
import { volumeStatusFamily } from "../../atoms/hangingProtocolAtoms";
import { useSeriesPointerDrag } from "../../hooks/useSeriesPointerDrag";

interface SeriesThumbnailProps {
  thumbnail: ThumbnailData;
  isAssigned?: boolean;
  onClick?: () => void;
  /** Re-run the thumbnail load. Surfaced as a clickable target inside the
   * Failed state so users don't have to reload the page when a single
   * preview times out. */
  onRetry?: () => void;
  /** Right-click handler — opens the reformat menu (owned by the grid, which
   *  renders one menu for the whole list rather than one per thumbnail). */
  onContextMenu?: (e: React.MouseEvent) => void;
}

export const SeriesThumbnail: React.FC<SeriesThumbnailProps> = ({
  thumbnail,
  isAssigned = false,
  onClick,
  onRetry,
  onContextMenu,
}) => {
  // Convert canvas to data URL synchronously during render
  // This ensures the img src is set immediately, avoiding broken image icons
  const imageSrc = useMemo(() => {
    if (thumbnail.canvas) {
      try {
        return thumbnail.canvas.toDataURL();
      } catch {
        return undefined;
      }
    }
    return undefined;
  }, [thumbnail.canvas]);

  // Pointer-based drag (replaces native HTML5 DnD, which Safari won't initiate
  // on a <div>). startDrag is invoked from onPointerDown below.
  const { startDrag } = useSeriesPointerDrag();

  // Full series description
  const displayName = thumbnail.seriesDescription || `Series ${thumbnail.seriesId.slice(-8)}`;

  // Track whether this series' volume has finished loading into a viewport.
  // Used only to trigger a one-shot thumbnail reload once the middle-slice
  // bytes are warm in cache (see the auto-retry effect below). Thumbnails are
  // no longer dimmed by load state — they always show the standard-windowed
  // preview at full strength (the assigned dot marks the "live" series).
  const volumeStatus = useAtomValue(volumeStatusFamily(thumbnail.seriesId));
  const isVolumeReady = volumeStatus === "ready";

  // Auto-retry the preview load when this series' volume finishes loading
  // into a viewport. At cold-start the sidebar thumbnails race against the
  // viewport's volume slice fetches and lose against Chrome's 6-connection
  // limit, timing out. Once the volume completes, the middle-slice bytes
  // are warm in the wadouri cache, so the retry is a fast cache hit.
  // Guarded to one auto-retry per ready event so a genuinely-broken URL
  // doesn't loop.
  const autoRetriedRef = useRef(false);
  useEffect(() => {
    if (!isVolumeReady) {
      autoRetriedRef.current = false;
      return;
    }
    if (thumbnail.error && !autoRetriedRef.current && onRetry) {
      autoRetriedRef.current = true;
      onRetry();
    }
  }, [isVolumeReady, thumbnail.error, onRetry]);

  return (
    <div
      onPointerDown={
        !thumbnail.loading && !thumbnail.error ? (e) => startDrag(e, thumbnail.seriesId) : undefined
      }
      {...(onClick ? { onClick } : {})}
      {...(onContextMenu ? { onContextMenu } : {})}
      className={`
        sidebar-thumbnail flex items-start gap-2 p-1.5 cursor-grab active:cursor-grabbing
        ${isAssigned ? "sidebar-thumbnail-active" : ""}
        ${thumbnail.loading || thumbnail.error ? "cursor-default" : ""}
      `}
      title={thumbnail.seriesDescription || thumbnail.seriesId}
    >
      {/* Thumbnail Image - smaller 60x60 */}
      <div className="w-[60px] h-[60px] shrink-0 rounded overflow-hidden flex items-center justify-center bg-surface-2">
        {thumbnail.loading ? (
          <FaSpinner className="animate-spin w-4 h-4 text-fg-muted" />
        ) : thumbnail.error ? (
          onRetry ? (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onRetry();
              }}
              className="text-[8px] text-warn hover:text-white text-center px-1 leading-tight"
              title={thumbnail.error ? `${thumbnail.error} — click to retry` : "Retry preview load"}
            >
              Failed
              <br />
              <span className="underline opacity-80">Retry</span>
            </button>
          ) : (
            <div
              className="text-[8px] text-warn text-center px-1"
              title={thumbnail.error || undefined}
            >
              Failed
            </div>
          )
        ) : imageSrc ? (
          <img
            src={imageSrc}
            alt={displayName}
            className="w-full h-full object-contain"
            draggable={false}
          />
        ) : (
          <div className="text-[8px] text-fg-muted">No preview</div>
        )}
      </div>

      {/* Series Info - right side, full name with wrap */}
      <div className="flex-1 min-w-0 py-0.5">
        <div className="text-[10px] text-fg leading-tight line-clamp-2">{displayName}</div>
        <div className="text-[9px] sidebar-text-muted mt-0.5">{thumbnail.instanceCount} slices</div>
        {/* Debug: Show series UID prefix for verification */}
        <div
          className="text-[8px] text-fg-muted mt-0.5 font-mono truncate opacity-60"
          title={thumbnail.seriesId}
        >
          {thumbnail.seriesId.slice(-12)}
        </div>
      </div>

      {/* Assigned indicator */}
      {isAssigned && <div className="w-1.5 h-1.5 bg-signal rounded-full shrink-0 mt-1" />}
    </div>
  );
};

export default SeriesThumbnail;
